// ── Counter Intelligence: in-chat reports ────────────────────────────────────
// Wires the Reports page's reports into the chat drawer so a plain-English
// question ("what was my inventory value for August", "review my purchase
// history", "what was my COGS") suggests the matching report, asks for just
// the detail it needs (which count / what period / what date range), then
// renders the result inline with Download / Email links.
//
// Most generators here are self-contained because Reports.jsx is bound to
// component state. Price changes deliberately share priceChangeReport.mjs with
// the Reports page so future automated insights and on-screen results cannot
// drift apart.
//
// Source-of-truth rules:
//   - Inventory value/totals/review come from completed count sessions, valued
//     with the same snapshot + pricing helpers (countItemReporting) the
//     Reports page uses.
//   - Actual COGS comes ONLY from saved End of Period reports (pnl_reports),
//     rendered through the same buildEopReportDescriptor as the Reports page.
//     RUNNING End of Period stays in the wizard (it needs manual inputs); the
//     chat reads saved results and points there when none exist.
//
// Intent parsing and session lookup live in ciReportQuery.mjs; this module
// holds the generators (and the original keyword matcher, kept for tests).

import { reportingSnapshotFromCountItem, unitValuePriceFromCountItem } from './countItemReporting';
import { buildEopReportDescriptor } from './eopReportDescriptor';
import {
  buildPriceChangeReportData,
  PRICE_CHANGE_HISTORY_SELECT,
  PRICE_CHANGE_ITEM_SELECT,
  loadPriceChangeCategoryMappings,
  summarizePriceChangeGroups,
} from './priceChangeReport.mjs';

// ── Formatting (mirrors Reports.jsx so output is identical) ──────────────────
const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
export const formatCurrency = (value) => currencyFormatter.format(Number.isFinite(value) ? value : 0);
export const formatNumber = (value) => numberFormatter.format(Number.isFinite(value) ? value : 0);

const formatWrittenDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  const day = parsed.getDate();
  const suffix = [11, 12, 13].includes(day % 100) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
  const month = parsed.toLocaleDateString('en-US', { month: 'long' });
  return `${month} ${day}${suffix} ${parsed.getFullYear()}`;
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleString();
};

const formatShortDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PO_STATUS_LABELS = {
  sent: 'Sent by email',
  delivered: 'Sent by email',
  opened: 'Sent by email',
  clicked: 'Sent by email',
  pending_approval: 'Pending approval',
  blocked: 'Not sent — no vendor email',
  blocked_no_email: 'Marked sent outside Countertop',
  sent_outside_countertop: 'Marked sent outside Countertop',
  bounced: 'Bounced',
  complained: 'Complaint received',
  sending: 'Sending',
  draft: 'Draft',
  error: 'Action needed',
};
const poStatusLabel = (status) => PO_STATUS_LABELS[status]
  || String(status || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ── Store / user context for the descriptor header ───────────────────────────
export const getReportStoreName = () => {
  try {
    const cur = JSON.parse(localStorage.getItem('currentStore') || 'null');
    if (cur?.name) return cur.name;
  } catch (_) { /* no-op */ }
  try {
    const sel = JSON.parse(localStorage.getItem('selectedStore') || 'null');
    if (sel?.name) return sel.name;
  } catch (_) { /* no-op */ }
  return localStorage.getItem('selectedStoreName') || 'Your Store';
};

export const getReportCreatedBy = () => {
  try {
    const cu = JSON.parse(localStorage.getItem('currentUser') || 'null');
    if (cu) {
      const name = [cu.first_name, cu.last_name].filter(Boolean).join(' ').trim();
      return name || cu.name || cu.email || '';
    }
  } catch (_) { /* no-op */ }
  return '';
};

// ── Intent matching ──────────────────────────────────────────────────────────
// Each report is recognised from a small set of phrasings. We match the user's
// wording directly (before the server classifier) so "see what prices changed"
// reliably lands on the price-change *report* rather than the price-*update*
// flow it sits next to.
const REPORT_TOPICS = [
  {
    id: 'inventory_totals',
    triggers: [
      'inventory total', 'inventory totals', 'inventory value', 'value of my inventory',
      'value of inventory', 'total inventory', 'how much inventory', 'what my inventory is worth',
      'inventory worth', 'report my inventory', 'inventory report', 'count totals', 'count total value',
    ],
  },
  {
    id: 'purchase_history',
    triggers: [
      'purchase history', 'purchasing history', 'order history', 'purchase order report',
      'purchase orders report', 'review my purchase', 'review my orders', 'my past orders',
      'orders i sent', 'orders i have sent', 'what i ordered', 'what have i ordered',
      'past purchase', 'see my orders', 'see my purchase',
    ],
  },
  {
    id: 'price_changes',
    triggers: [
      'prices changed', 'price changed', 'prices have changed', 'price have changed',
      'prices that changed', 'what prices changed', 'which prices changed', 'item prices changed',
      'item price change', 'price change report', 'price changes', 'how prices changed',
      'what changed in price', 'see price change', 'review price change',
    ],
  },
];

export const matchReportTopic = (raw) => {
  const t = (raw || '').toLowerCase();
  if (!t) return null;
  for (const topic of REPORT_TOPICS) {
    if (topic.triggers.some((kw) => t.includes(kw))) return topic.id;
  }
  return null;
};

const CANCEL_RE = /\b(no|not now|nope|nah|later|never ?mind|cancel|stop|forget it|none)\b/i;
export const isReportCancel = (text) => CANCEL_RE.test(String(text || ''));

// ── Pending follow-up context ────────────────────────────────────────────────
const PENDING_TTL_MS = 30 * 60 * 1000;
// expires_at makes report pendings visible to the drawer's model-first router
// (which treats a pending without expires_at as closed), so a NEW question
// asked mid-disambiguation clears the stale pending instead of being swallowed.
export const buildReportPending = (extra) => ({
  kind: 'pending_report',
  createdAt: new Date().toISOString(),
  expires_at: new Date(Date.now() + PENDING_TTL_MS).toISOString(),
  ...extra,
});
export const isReportPendingExpired = (pending) => {
  if (!pending?.createdAt) return false;
  return Date.now() - new Date(pending.createdAt).getTime() > PENDING_TTL_MS;
};

// ── Inventory counts: chips + reply resolution ────────────────────────────────
// Session lookup lives in ciReportQuery.mjs (listReportSessionsForAI), which
// mirrors the Reports page's eligibility filters exactly.
export const inventoryCountChips = (sessions) =>
  (sessions || []).map((s) => ({
    key: `count-${s.id}`,
    label: s.date ? `${s.name} · ${formatShortDate(s.date)}` : s.name,
  }));

// Resolve a chip label or a typed reply ("the latest", "Friday count") to one
// of the offered sessions. Exact chip label and name matches run BEFORE the
// "latest" shortcut so a count named e.g. "Last Friday Count" resolves to
// itself when its chip is tapped, not to the newest session.
export const resolveInventoryCountReply = (text, sessions) => {
  const list = sessions || [];
  if (!list.length) return null;
  const t = (text || '').trim().toLowerCase();
  if (!t) return null;
  for (const s of list) {
    const chip = (s.date ? `${s.name} · ${formatShortDate(s.date)}` : s.name).toLowerCase();
    if (chip === t) return s;
  }
  for (const s of list) {
    const name = String(s.name || '').toLowerCase();
    if (name && (t.includes(name) || name.includes(t))) return s;
  }
  if (/\b(latest|most recent|recent|newest|last|first|top)\b/.test(t)) return list[0];
  return null;
};

// ── Scope matching (item / category / location / P&L filters) ────────────────
// "wine" matches the Wine category, "food" the Food P&L bucket, "cabernet" the
// item description, "bar" the location — one loose contains-match over all of
// them, with case/punctuation/plurals folded.
const normScopeText = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
const foldPlurals = (s) => s.replace(/s\b/g, '');
export const scopeMatchesItem = (item, scope) => {
  const needle = foldPlurals(normScopeText(scope));
  if (!needle) return true;
  const hay = foldPlurals(normScopeText([
    item.description, item.category, item.plCategory, item.location,
  ].filter(Boolean).join(' | ')));
  return hay.includes(needle);
};

/**
 * The count-session summary "tool": every counted line of a completed count,
 * valued with the SAME snapshot + pricing rules as the Reports page
 * (snapshot_at_close ?? item_snapshot; case price first), plus rollups.
 * Optionally filtered to a scope ("wine", "food", "bar", "cabernet").
 */
export const getReportSessionSummaryForAI = async ({ supabase, session, scope = null }) => {
  const { data, error } = await supabase
    .from('count_items')
    .select('count_session_id, actual_quantity, item_snapshot, snapshot_at_close, pl_category_snapshot, location')
    .eq('count_session_id', session.id);
  if (error) throw error;

  const allItems = (data || []).map((row) => {
    const snap = reportingSnapshotFromCountItem(row);
    const quantity = parseFloat(row.actual_quantity) || 0;
    return {
      description: snap.description || '(unknown item)',
      category: snap.category || 'Uncategorized',
      plCategory: row.pl_category_snapshot || null,
      location: row.location || snap.location || '',
      unit: snap.unit || '',
      quantity,
      value: quantity * unitValuePriceFromCountItem(row),
    };
  });
  const items = scope ? allItems.filter((it) => scopeMatchesItem(it, scope)) : allItems;

  const byCategory = new Map();
  let totalValue = 0;
  let totalQuantity = 0;
  items.forEach((it) => {
    totalValue += it.value;
    totalQuantity += it.quantity;
    byCategory.set(it.category, (byCategory.get(it.category) || 0) + it.value);
  });
  const allTotalValue = scope ? allItems.reduce((s, it) => s + it.value, 0) : totalValue;

  return { items, byCategory, totalValue, totalQuantity, allTotalValue, allItemCount: allItems.length };
};

export const generateInventoryValueReport = async ({ supabase, storeId, session, scope = null, storeName, createdBy }) => {
  const summary = await getReportSessionSummaryForAI({ supabase, session, scope });
  const { byCategory, totalValue, allTotalValue } = summary;

  const categoryData = [...byCategory.entries()]
    .map(([category, value]) => ({ category, value }))
    .sort((a, b) => b.value - a.value);

  const rows = categoryData.map((cat) => ({
    category: cat.category,
    totalValueFmt: formatCurrency(cat.value),
    pctFmt: `${(totalValue > 0 ? (cat.value / totalValue) * 100 : 0).toFixed(1)}%`,
  }));

  const generatedAt = new Date().toISOString();
  const scopeLabel = scope ? scope.charAt(0).toUpperCase() + scope.slice(1) : '';
  const descriptor = {
    store: { name: storeName },
    report: {
      title: 'Inventory Value',
      subtitle: session.date ? formatWrittenDate(session.date) : formatWrittenDate(generatedAt),
      counters: [],
      createdBy: createdBy || '',
      generatedAt,
      meta: [
        { label: 'Session', value: session.name },
        { label: 'Mode', value: 'Inventory' },
        ...(scope ? [{ label: 'Filter', value: scopeLabel }] : []),
      ],
    },
    stats: [
      { label: 'Categories', value: formatNumber(categoryData.length) },
      { label: 'Total Value', value: formatCurrency(totalValue) },
    ],
    sections: [{
      columns: [
        { key: 'category', label: 'Category', align: 'text' },
        { key: 'totalValueFmt', label: 'Total Value', align: 'currency', width: '110px' },
        { key: 'pctFmt', label: '% of Inventory', align: 'number', width: '110px' },
      ],
      rows,
      totals: { category: 'Grand Total', totalValueFmt: formatCurrency(totalValue), pctFmt: '100.0%' },
    }],
  };

  const sessionLabel = session.date ? `${session.name} (completed ${formatWrittenDate(session.date)})` : session.name;
  let summaryText;
  if (!rows.length) {
    summaryText = scope
      ? `I checked ${sessionLabel} for counted items matching “${scope}” but didn't find any.`
      : `I checked ${sessionLabel} for counted items to value, but didn't find any yet.`;
  } else if (scope) {
    summaryText = `Here is the inventory value data you requested. ${scopeLabel} in ${sessionLabel} is worth ${formatCurrency(totalValue)} (out of ${formatCurrency(allTotalValue)} total).`;
  } else {
    summaryText = `Here is the inventory value data you requested. ${session.name} is worth ${formatCurrency(totalValue)} across ${formatNumber(categoryData.length)} categories.`;
  }

  return {
    descriptor,
    empty: rows.length === 0,
    summary: summaryText,
    emailLines: [`Total value: ${formatCurrency(totalValue)}`, `Categories: ${formatNumber(categoryData.length)}`],
    csvFilename: `inventory_value_${generatedAt.slice(0, 10)}.csv`,
    link: { href: '/reports.html', label: 'Open Reports →' },
  };
};

// ── Inventory Totals & Count Review (count-level, quantity-first) ────────────
const buildCountLevelResult = ({ variant, session, summary, scope, storeName, createdBy }) => {
  const { items, totalValue } = summary;
  const generatedAt = new Date().toISOString();
  const isReview = variant === 'review';
  const sorted = [...items].sort((a, b) => (isReview
    ? (a.location || '').localeCompare(b.location || '') || a.description.localeCompare(b.description)
    : b.value - a.value || a.description.localeCompare(b.description)));

  const rows = sorted.map((it) => ({
    description: it.description,
    category: it.category,
    location: it.location || '—',
    quantityFmt: formatNumber(it.quantity),
    unit: it.unit || '—',
    valueFmt: formatCurrency(it.value),
  }));

  // Quantities only sum to one number when EVERY row carries the same unit —
  // a blank-unit row makes the total a mixed-unit figure, so itemize instead.
  const units = new Set(items.map((it) => normScopeText(it.unit)).filter(Boolean));
  const uniformUnit = (units.size === 1 && items.every((it) => it.unit))
    ? items.find((it) => it.unit)?.unit
    : null;
  const totalQuantity = summary.totalQuantity;
  const locations = new Set(items.map((it) => it.location).filter(Boolean));

  const stats = [
    { label: 'Items', value: formatNumber(items.length) },
    ...(isReview ? [{ label: 'Locations', value: formatNumber(locations.size) }] : []),
    ...(!isReview && uniformUnit ? [{ label: 'Total Quantity', value: `${formatNumber(totalQuantity)} ${uniformUnit}` }] : []),
    { label: 'Total Value', value: formatCurrency(totalValue) },
  ];

  const scopeLabel = scope ? scope.charAt(0).toUpperCase() + scope.slice(1) : '';
  const descriptor = {
    store: { name: storeName },
    report: {
      title: isReview ? 'Count Review' : 'Inventory Totals',
      subtitle: session.date ? formatWrittenDate(session.date) : formatWrittenDate(generatedAt),
      counters: [],
      createdBy: createdBy || '',
      generatedAt,
      meta: [
        { label: 'Session', value: session.name },
        ...(scope ? [{ label: 'Filter', value: scopeLabel }] : []),
      ],
    },
    stats,
    sections: [{
      columns: isReview
        ? [
          { key: 'location', label: 'Location', align: 'text', width: '120px' },
          { key: 'description', label: 'Item', align: 'text' },
          { key: 'quantityFmt', label: 'Qty', align: 'number', width: '70px' },
          { key: 'unit', label: 'Unit', align: 'text', width: '80px' },
          { key: 'valueFmt', label: 'Value', align: 'currency', width: '100px' },
        ]
        : [
          { key: 'description', label: 'Item', align: 'text' },
          { key: 'category', label: 'Category', align: 'text', width: '110px' },
          { key: 'location', label: 'Location', align: 'text', width: '100px' },
          { key: 'quantityFmt', label: 'Qty', align: 'number', width: '70px' },
          { key: 'unit', label: 'Unit', align: 'text', width: '80px' },
          { key: 'valueFmt', label: 'Value', align: 'currency', width: '100px' },
        ],
      rows,
      totals: isReview
        ? { location: 'Total', description: '', quantityFmt: '', unit: '', valueFmt: formatCurrency(totalValue) }
        : { description: 'Total', category: '', location: '', quantityFmt: uniformUnit ? formatNumber(totalQuantity) : '', unit: uniformUnit || '', valueFmt: formatCurrency(totalValue) },
    }],
  };

  const sessionLabel = session.date ? `${session.name} (completed ${formatWrittenDate(session.date)})` : session.name;
  let text;
  if (!rows.length) {
    text = scope
      ? `I checked ${sessionLabel} but didn't find anything matching “${scope}” that was counted.`
      : `I checked ${sessionLabel} but didn't find any counted items yet.`;
  } else if (isReview) {
    text = `Here is the count review you requested. ${sessionLabel} counted ${formatNumber(items.length)} item${items.length === 1 ? '' : 's'}${locations.size ? ` across ${formatNumber(locations.size)} location${locations.size === 1 ? '' : 's'}` : ''}, worth ${formatCurrency(totalValue)}.${scope ? ` Filtered to “${scope}”.` : ''}`;
  } else if (scope && uniformUnit) {
    text = `Here is the inventory total you requested. You counted ${formatNumber(totalQuantity)} ${uniformUnit} of “${scope}” in ${sessionLabel}, worth ${formatCurrency(totalValue)}.`;
  } else if (scope) {
    text = `Here is the inventory total you requested. ${formatNumber(items.length)} counted item${items.length === 1 ? '' : 's'} match “${scope}” in ${sessionLabel}, worth ${formatCurrency(totalValue)} — quantities are itemized below (mixed units).`;
  } else {
    text = `Here is the inventory total you requested. ${sessionLabel} has ${formatNumber(items.length)} counted item${items.length === 1 ? '' : 's'} worth ${formatCurrency(totalValue)} — counted quantities are itemized below.`;
  }

  return {
    descriptor,
    empty: rows.length === 0,
    summary: text,
    emailLines: [
      `Items: ${formatNumber(items.length)}`,
      ...(uniformUnit ? [`Total quantity: ${formatNumber(totalQuantity)} ${uniformUnit}`] : []),
      `Total value: ${formatCurrency(totalValue)}`,
    ],
    csvFilename: `${isReview ? 'count_review' : 'inventory_totals'}_${generatedAt.slice(0, 10)}.csv`,
    link: { href: '/reports.html', label: 'Open Reports →' },
  };
};

/** Counted quantities for a completed count ("how many … did we count?"). */
export const generateInventoryTotalsReport = async ({ supabase, storeId, session, scope = null, storeName, createdBy }) => {
  const summary = await getReportSessionSummaryForAI({ supabase, session, scope });
  return buildCountLevelResult({ variant: 'totals', session, summary, scope, storeName, createdBy });
};

/** What a completed count session contains ("what did I count yesterday?"). */
export const generateCountReviewReport = async ({ supabase, storeId, session, scope = null, storeName, createdBy }) => {
  const summary = await getReportSessionSummaryForAI({ supabase, session, scope });
  return buildCountLevelResult({ variant: 'review', session, summary, scope, storeName, createdBy });
};

// ── Purchase history: pick a period ──────────────────────────────────────────
export const PURCHASE_PERIODS = [
  { key: 'po-30', label: 'Last 30 days', days: 30 },
  { key: 'po-90', label: 'Last 90 days', days: 90 },
  { key: 'po-all', label: 'All time', days: null },
];
export const purchaseHistoryChips = () => PURCHASE_PERIODS.map((p) => ({ key: p.key, label: p.label }));

export const resolvePurchasePeriodReply = (text) => {
  const t = (text || '').trim().toLowerCase();
  if (!t) return null;
  if (/\ball\b|all time|everything|entire|every order/.test(t)) return { days: null, label: 'All time' };
  if (/\b90\b|ninety|quarter|three month|3 month/.test(t)) return { days: 90, label: 'Last 90 days' };
  if (/\b30\b|thirty|one month|1 month|last month|this month/.test(t)) return { days: 30, label: 'Last 30 days' };
  const num = t.match(/(\d{1,4})\s*(day|week|month)/);
  if (num) {
    const n = parseInt(num[1], 10);
    const mult = num[2] === 'week' ? 7 : num[2] === 'month' ? 30 : 1;
    if (Number.isFinite(n) && n > 0) return { days: n * mult, label: `Last ${n} ${num[2]}${n === 1 ? '' : 's'}` };
  }
  return null;
};

// Purchase orders that never became real purchases — excluded from "what did I
// buy / how much did I spend" answers (the browse-style report keeps them and
// shows their status instead). Mirrors purchaseOrderService's inactive set.
const PO_STATUSES_EXCLUDED_FROM_SPEND = new Set([
  'draft', 'pending_approval', 'blocked', 'error', 'canceled', 'voided', 'replaced', 'revised',
]);

const normVendorText = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

const fetchPurchaseOrders = async ({ supabase, storeId, sinceDays, startDate, endDate, vendorScope, excludeInactive }) => {
  let query = supabase
    .from('purchase_orders')
    .select('id, po_number, vendor_name_snapshot, ordered_by_name, ordered_by_email, total, line_count, delivery_status, created_at')
    .eq('store_id', storeId)
    .order('created_at', { ascending: false })
    .limit(400);
  if (startDate) {
    query = query.gte('created_at', new Date(`${startDate}T00:00:00`).toISOString());
  } else if (Number.isFinite(sinceDays) && sinceDays != null) {
    const since = new Date();
    since.setDate(since.getDate() - sinceDays);
    query = query.gte('created_at', since.toISOString());
  }
  if (endDate) query = query.lte('created_at', new Date(`${endDate}T23:59:59.999`).toISOString());
  const { data, error } = await query;
  if (error) throw error;

  let orders = data || [];
  // The cap is hit BEFORE the client-side filters, so a full page means the
  // window may extend past what we fetched — totals must say so.
  const truncated = orders.length >= 400;
  if (excludeInactive) orders = orders.filter((r) => !PO_STATUSES_EXCLUDED_FROM_SPEND.has(r.delivery_status));
  if (vendorScope) {
    const needle = normVendorText(vendorScope);
    orders = orders.filter((r) => normVendorText(r.vendor_name_snapshot).includes(needle));
  }
  return { orders, truncated };
};
const PO_TRUNCATION_NOTE = 'Covers the 400 most recent orders in this window — older orders are not included in the total.';

export const generatePurchaseHistoryReport = async ({
  supabase, storeId, sinceDays, periodLabel, storeName, createdBy,
  startDate = null, endDate = null, vendorScope = null, excludeInactive = false,
}) => {
  const { orders, truncated } = await fetchPurchaseOrders({ supabase, storeId, sinceDays, startDate, endDate, vendorScope, excludeInactive });
  const totalValue = orders.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);

  const rows = orders.map((row) => ({
    poNumber: row.po_number || '—',
    vendorName: row.vendor_name_snapshot || 'Unknown Vendor',
    orderedByFmt: row.ordered_by_name || row.ordered_by_email || '',
    createdFmt: formatDateTime(row.created_at),
    statusFmt: poStatusLabel(row.delivery_status),
    totalFmt: formatCurrency(parseFloat(row.total) || 0),
  }));

  const generatedAt = new Date().toISOString();
  const descriptor = {
    store: { name: storeName },
    report: {
      title: 'Purchase Orders Report',
      subtitle: `${formatWrittenDate(generatedAt)}${periodLabel ? ` — ${periodLabel}` : ''}`,
      counters: [],
      createdBy: createdBy || '',
      generatedAt,
      meta: [
        { label: 'Period', value: periodLabel || 'All time' },
        { label: 'Purchase Orders', value: formatNumber(orders.length) },
        ...(vendorScope ? [{ label: 'Vendor', value: vendorScope }] : []),
        ...(excludeInactive ? [{ label: 'Included', value: 'Sent orders only (drafts and canceled excluded)' }] : []),
        ...(truncated ? [{ label: 'Note', value: PO_TRUNCATION_NOTE }] : []),
      ],
    },
    stats: [
      { label: 'Purchase Orders', value: formatNumber(orders.length) },
      { label: 'Total Value', value: formatCurrency(totalValue) },
    ],
    sections: [{
      columns: [
        { key: 'poNumber', label: 'PO #', align: 'text', width: '90px' },
        { key: 'vendorName', label: 'Vendor', align: 'text' },
        { key: 'orderedByFmt', label: 'Ordered By', align: 'text', width: '130px' },
        { key: 'createdFmt', label: 'Created', align: 'text', width: '120px' },
        { key: 'statusFmt', label: 'Status', align: 'text', width: '120px' },
        { key: 'totalFmt', label: 'Total', align: 'currency', width: '110px' },
      ],
      rows,
    }],
  };

  const vendorPhrase = vendorScope ? ` from ${vendorScope}` : '';
  const sentOnlyPhrase = excludeInactive ? ' (sent orders only — drafts and canceled excluded)' : '';
  const truncationPhrase = truncated ? ` ${PO_TRUNCATION_NOTE}` : '';
  const summary = rows.length
    ? `Here is the purchase history you requested. I found ${formatNumber(orders.length)} purchase order${orders.length === 1 ? '' : 's'}${vendorPhrase} totaling ${formatCurrency(totalValue)}${periodLabel ? ` (${periodLabel.toLowerCase()})` : ''}${sentOnlyPhrase}.${truncationPhrase}`
    : `I checked for purchase orders${vendorPhrase}${periodLabel ? ` for ${periodLabel.toLowerCase()}` : ''} but didn't find any.${truncationPhrase}`;
  return {
    descriptor,
    empty: rows.length === 0,
    summary,
    emailLines: [`Purchase orders: ${formatNumber(orders.length)}`, `Total value: ${formatCurrency(totalValue)}`],
    csvFilename: `purchase_orders_${generatedAt.slice(0, 10)}.csv`,
    link: { href: '/reports.html', label: 'Open Reports →' },
  };
};

// ── Purchase history, line-item level ("show wine purchases for September") ───
export const generatePurchaseItemHistoryReport = async ({
  supabase, storeId, startDate = null, endDate = null, sinceDays = null, periodLabel = '',
  vendorScope = null, itemScope = null, storeName, createdBy,
}) => {
  const { orders, truncated } = await fetchPurchaseOrders({
    supabase, storeId, sinceDays, startDate, endDate, vendorScope, excludeInactive: true,
  });
  const orderById = new Map(orders.map((o) => [o.id, o]));

  // Fetch line items in id chunks so the request stays well-formed even for
  // stores with hundreds of orders in the window.
  const ids = orders.map((o) => o.id);
  const lines = [];
  for (let i = 0; i < ids.length; i += 100) {
    const { data, error } = await supabase
      .from('purchase_order_items')
      .select('purchase_order_id, description, unit, order_quantity, unit_price, line_total, snapshot')
      .in('purchase_order_id', ids.slice(i, i + 100));
    if (error) throw error;
    (data || []).forEach((row) => lines.push(row));
  }

  const needle = itemScope ? foldPlurals(normScopeText(itemScope)) : '';
  const matched = lines.filter((row) => {
    if (!needle) return true;
    const snap = (row.snapshot && typeof row.snapshot === 'object') ? row.snapshot : {};
    const hay = foldPlurals(normScopeText([row.description, snap.category, snap.brand].filter(Boolean).join(' | ')));
    return hay.includes(needle);
  });

  const rows = matched.map((row) => {
    const po = orderById.get(row.purchase_order_id) || {};
    return {
      description: row.description || '(unknown item)',
      vendorName: po.vendor_name_snapshot || 'Unknown Vendor',
      poNumber: po.po_number || '—',
      dateFmt: formatShortDate(po.created_at),
      quantityFmt: formatNumber(parseFloat(row.order_quantity) || 0),
      unit: row.unit || '—',
      lineTotalFmt: formatCurrency(parseFloat(row.line_total) || 0),
      _total: parseFloat(row.line_total) || 0,
      _poId: row.purchase_order_id,
    };
  });
  const totalValue = rows.reduce((sum, r) => sum + r._total, 0);
  const orderCount = new Set(rows.map((r) => r._poId)).size;

  const generatedAt = new Date().toISOString();
  const scopePhrase = itemScope ? `“${itemScope}” ` : '';
  const summary = rows.length
    ? `Here is the purchase item history you requested. You bought ${formatCurrency(totalValue)} of ${scopePhrase}across ${formatNumber(rows.length)} line item${rows.length === 1 ? '' : 's'} in ${formatNumber(orderCount)} sent order${orderCount === 1 ? '' : 's'}${vendorScope ? ` from ${vendorScope}` : ''}${periodLabel ? ` (${periodLabel.toLowerCase()})` : ''}.`
    : `I checked for ${scopePhrase}purchases${vendorScope ? ` from ${vendorScope}` : ''}${periodLabel ? ` for ${periodLabel.toLowerCase()}` : ''} but didn't find any.`;
  const descriptor = {
    store: { name: storeName },
    report: {
      title: 'Purchase History — Items',
      subtitle: `${formatWrittenDate(generatedAt)}${periodLabel ? ` — ${periodLabel}` : ''}`,
      counters: [],
      createdBy: createdBy || '',
      generatedAt,
      meta: [
        { label: 'Period', value: periodLabel || 'All time' },
        ...(itemScope ? [{ label: 'Items', value: itemScope }] : []),
        ...(vendorScope ? [{ label: 'Vendor', value: vendorScope }] : []),
        { label: 'Included', value: 'Sent orders only (drafts and canceled excluded)' },
        ...(truncated ? [{ label: 'Note', value: PO_TRUNCATION_NOTE }] : []),
      ],
    },
    stats: [
      { label: 'Line Items', value: formatNumber(rows.length) },
      { label: 'Orders', value: formatNumber(orderCount) },
      { label: 'Total', value: formatCurrency(totalValue) },
    ],
    sections: [{
      columns: [
        { key: 'description', label: 'Item', align: 'text' },
        { key: 'vendorName', label: 'Vendor', align: 'text', width: '120px' },
        { key: 'poNumber', label: 'PO #', align: 'text', width: '90px' },
        { key: 'dateFmt', label: 'Ordered', align: 'text', width: '110px' },
        { key: 'quantityFmt', label: 'Qty', align: 'number', width: '60px' },
        { key: 'unit', label: 'Unit', align: 'text', width: '80px' },
        { key: 'lineTotalFmt', label: 'Total', align: 'currency', width: '100px' },
      ],
      rows: rows.map(({ _total, _poId, ...r }) => r),
      totals: { description: 'Total', lineTotalFmt: formatCurrency(totalValue) },
    }],
  };

  return {
    descriptor,
    empty: rows.length === 0,
    summary: summary + (truncated ? ` ${PO_TRUNCATION_NOTE}` : ''),
    emailLines: [`Line items: ${formatNumber(rows.length)}`, `Orders: ${formatNumber(orderCount)}`, `Total: ${formatCurrency(totalValue)}`],
    csvFilename: `purchase_items_${generatedAt.slice(0, 10)}.csv`,
    link: { href: '/reports.html', label: 'Open Reports →' },
  };
};

// ── Price changes: pick a date range ─────────────────────────────────────────
const isoDay = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const rangeFromDays = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { startDate: isoDay(start), endDate: isoDay(end) };
};

export const PRICE_RANGES = [
  { key: 'pc-30', label: 'Last 30 days' },
  { key: 'pc-90', label: 'Last 90 days' },
  { key: 'pc-ytd', label: 'This year' },
  { key: 'pc-365', label: 'Last 12 months' },
];
export const priceRangeChips = () => PRICE_RANGES.map((r) => ({ key: r.key, label: r.label }));

export const resolvePriceRangeReply = (text) => {
  const t = (text || '').trim().toLowerCase();
  if (!t) return null;
  if (/this year|year to date|ytd/.test(t)) {
    const end = new Date();
    return { ...{ startDate: isoDay(new Date(end.getFullYear(), 0, 1)), endDate: isoDay(end) }, label: 'This year' };
  }
  if (/12 month|twelve month|last year|past year|365/.test(t)) return { ...rangeFromDays(365), label: 'Last 12 months' };
  if (/\b90\b|ninety|quarter|three month|3 month/.test(t)) return { ...rangeFromDays(90), label: 'Last 90 days' };
  if (/\b30\b|thirty|one month|1 month|last month|this month/.test(t)) return { ...rangeFromDays(30), label: 'Last 30 days' };
  const num = t.match(/(\d{1,4})\s*(day|week|month)/);
  if (num) {
    const n = parseInt(num[1], 10);
    const mult = num[2] === 'week' ? 7 : num[2] === 'month' ? 30 : 1;
    if (Number.isFinite(n) && n > 0) return { ...rangeFromDays(n * mult), label: `Last ${n} ${num[2]}${n === 1 ? '' : 's'}` };
  }
  return null;
};

const PRICE_SOURCE_LABELS = {
  ai_price_import: 'AI Import',
  import_approval: 'Import',
  item_editor: 'Item Editor',
  web_count: 'Web Count',
  mobile_count: 'Mobile Count',
  mobile_count_close: 'Mobile Count',
};

const normalizedPriceText = (value) => String(value || '').trim().toLowerCase();
const priceRowMatchesScope = (row, scope, scopeKind) => {
  const needle = normalizedPriceText(scope);
  if (!needle) return true;
  const categoryFields = [row.category, row.pnlCategory];
  const fields = scopeKind === 'category'
    ? categoryFields
    : (scopeKind === 'item' ? [row.itemName] : [row.itemName, row.vendor, ...categoryFields, row.location]);
  return fields.some((value) => normalizedPriceText(value).includes(needle));
};

const priceDirectionWord = (change) => (change > 0 ? 'increased' : (change < 0 ? 'decreased' : 'returned to its starting price'));
const priceAdvice = (scope) => {
  const t = normalizedPriceText(scope);
  if (/liquor|spirits|bar/.test(t)) return 'Review the largest bottle changes against the invoice and pack size, compare alternate brands or vendors, and check pour controls and menu pricing.';
  if (/food|meat|produce|seafood|dairy/.test(t)) return 'Review the largest changes against the invoice and pack size, compare vendor quotes or substitutions, and check yield, portions, and menu pricing.';
  return 'Review the largest changes against their invoices and pack sizes, then ask the vendor about increases and compare alternates, substitutions, or menu pricing.';
};

export const buildPriceInsightSummary = ({ rows, rangeLabel, scope, priceInsight }) => {
  const period = rangeLabel ? ` ${rangeLabel.toLowerCase()}` : ' in that range';
  const scopePhrase = scope ? ` for ${scope}` : '';
  if (!rows.length) {
    return `I checked for ${scope ? `${scope} ` : ''}price ${priceInsight === 'cost_drivers' ? 'increases' : 'changes'}${period}, but didn't find any recorded changes.`;
  }
  const top = rows[0];
  const change = `${top.absChange > 0 ? '+' : '−'}${formatCurrency(Math.abs(top.absChange))}`;
  const pct = top.pctChange == null ? '' : ` (${top.pctChange > 0 ? '+' : '−'}${Math.abs(top.pctChange).toFixed(1)}%)`;
  if (priceInsight === 'item_change') {
    return `Here is the price history you requested${scopePhrase}. I found ${formatNumber(top.changeCount)} recorded price change${top.changeCount === 1 ? '' : 's'}${period}: ${top.itemName} ${priceDirectionWord(top.absChange)} from ${formatCurrency(top.startPrice)} to ${formatCurrency(top.endPrice)} — ${change}${pct}.`;
  }
  if (priceInsight === 'movers') {
    return `Here is the price-change data you requested${scopePhrase}. The greatest mover${period} was ${top.itemName}: ${formatCurrency(top.startPrice)} to ${formatCurrency(top.endPrice)} (${change}${pct}).`;
  }
  if (priceInsight === 'cost_drivers') {
    const total = rows.reduce((sum, row) => sum + Math.max(0, row.absChange), 0);
    return `Here is the price-change data you requested${scopePhrase}. Price increases are one possible driver of high ${scope ? `${scope} ` : ''}costs, but this is not a full COGS diagnosis. ${formatNumber(rows.length)} item${rows.length === 1 ? '' : 's'} increased by ${formatCurrency(total)} in combined catalog-price movement${period}; the largest was ${top.itemName} at ${change}${pct}. That total is unweighted catalog movement, not purchase spend. ${priceAdvice(scope)}`;
  }
  const upCount = rows.filter((r) => r.absChange > 0).length;
  const downCount = rows.filter((r) => r.absChange < 0).length;
  const allUp = upCount === rows.length;
  const allDown = downCount === rows.length;
  const mixPhrase = allUp
    ? 'every change was an increase'
    : allDown
      ? 'every change was a decrease'
      : `${upCount} went up and ${downCount} went down`;
  const biggestMover = [...rows].sort((left, right) => Math.abs(right.absChange) - Math.abs(left.absChange))[0];
  const topMoverLine = biggestMover
    ? ` The largest movement was ${biggestMover.itemName} (${biggestMover.absChange > 0 ? '+' : '−'}${formatCurrency(Math.abs(biggestMover.absChange))}${biggestMover.pctChange == null ? '' : `, ${biggestMover.pctChange > 0 ? '+' : '−'}${Math.abs(biggestMover.pctChange).toFixed(1)}%`}).`
    : '';
  return `Here is the price-change data you requested${scopePhrase}${period}. I found ${formatNumber(rows.length)} row${rows.length === 1 ? '' : 's'} — ${mixPhrase}.${topMoverLine}`;
};

export const generatePriceChangeReport = async ({
  supabase, storeId, startDate, endDate, rangeLabel, storeName, createdBy,
  scope = null, priceScopeKind = null, priceDirection = null, priceInsight = null, priceSort = null,
}) => {
  const startTs = new Date(`${startDate}T00:00:00`);
  const endTs = new Date(`${endDate}T23:59:59`);
  const endIso = endTs.toISOString();
  // Look back far enough to know each item's price as it stood on the start date.
  const lookback = new Date(startTs);
  lookback.setFullYear(lookback.getFullYear() - 3);

  const [phRes, itemsRes, categoryMappings] = await Promise.all([
    supabase
      .from('price_history')
      .select(PRICE_CHANGE_HISTORY_SELECT)
      .eq('store_id', storeId)
      .gte('changed_at', lookback.toISOString())
      .lte('changed_at', endIso)
      .order('changed_at', { ascending: true })
      .limit(20000),
    supabase
      .from('inventory_items')
      .select(PRICE_CHANGE_ITEM_SELECT)
      .eq('store_id', storeId),
    loadPriceChangeCategoryMappings({ supabase, storeId }),
  ]);
  if (phRes.error) throw phRes.error;
  if (itemsRes.error) throw itemsRes.error;

  const priceChangeData = buildPriceChangeReportData({
    historyRows: phRes.data || [],
    inventoryItems: itemsRes.data || [],
    categoryMappings,
    startDate,
    endDate,
  });
  let reportRows = priceChangeData.rows
    .filter((row) => priceRowMatchesScope(row, scope, priceScopeKind))
    .filter((row) => priceDirection === 'increase' ? row.absChange > 0 : (priceDirection === 'decrease' ? row.absChange < 0 : true));
  reportRows = [...reportRows].sort((left, right) => {
    const leftMovement = Math.abs(left.absChange);
    const rightMovement = Math.abs(right.absChange);
    if (priceSort === 'lowest') return leftMovement - rightMovement || left.itemName.localeCompare(right.itemName);
    if (priceSort === 'greatest' || priceInsight === 'cost_drivers' || priceInsight === 'item_change') {
      return rightMovement - leftMovement || left.itemName.localeCompare(right.itemName);
    }
    return left.itemName.localeCompare(right.itemName);
  });


  const upCount = reportRows.filter((r) => r.absChange > 0).length;
  const downCount = reportRows.filter((r) => r.absChange < 0).length;

  const rows = reportRows.map((r) => {
    const isUp = (r.pctChange ?? 0) > 0;
    const isDown = (r.pctChange ?? 0) < 0;
    return {
      itemNameFmt: r.itemName,
      vendor: r.vendor || '—',
      category: r.category || '—',
      location: r.location || '—',
      startPriceFmt: r.startPrice != null ? `$${r.startPrice.toFixed(2)}` : '—',
      endPriceFmt: r.endPrice != null ? `$${r.endPrice.toFixed(2)}` : '—',
      absChangeFmt: r.absChange === 0 ? '$0.00' : `${r.absChange > 0 ? '+' : '−'}$${Math.abs(r.absChange).toFixed(2)}`,
      pctChangeFmt: r.pctChange === 0 ? '0.0%' : `${r.pctChange > 0 ? '+' : '−'}${Math.abs(r.pctChange).toFixed(1)}%`,
      sourceFmt: PRICE_SOURCE_LABELS[r.latestSource] || r.latestSource || '—',
      _style: isUp ? { color: '#2D6A20' } : isDown ? { color: '#B03020' } : undefined,
    };
  });

  const generatedAt = new Date().toISOString();
  const descriptor = {
    store: { name: storeName },
    report: {
      title: 'Price Change Report',
      subtitle: `${startDate} to ${endDate}`,
      counters: [],
      createdBy: createdBy || '',
      generatedAt,
      meta: [
        { label: 'Date Range', value: rangeLabel ? `${rangeLabel} (${startDate} to ${endDate})` : `${startDate} to ${endDate}` },
        ...(scope ? [{ label: priceScopeKind === 'category' ? 'Category / P&L' : 'Item', value: scope }] : []),
        ...(priceDirection ? [{ label: 'Direction', value: priceDirection === 'increase' ? 'Increases' : 'Decreases' }] : []),
      ],
    },
    stats: [
      { label: 'Up', value: String(upCount) },
      { label: 'Down', value: String(downCount) },
      { label: 'Total', value: String(reportRows.length) },
    ],
    sections: [{
      columns: [
        { key: 'itemNameFmt', label: 'Item Name', align: 'text' },
        { key: 'vendor', label: 'Vendor', align: 'text', width: '110px' },
        { key: 'category', label: 'Category', align: 'text', width: '110px' },
        { key: 'location', label: 'Location', align: 'text', width: '90px' },
        { key: 'startPriceFmt', label: 'Start', align: 'currency', width: '90px' },
        { key: 'endPriceFmt', label: 'End', align: 'currency', width: '90px' },
        { key: 'absChangeFmt', label: '$ Chg', align: 'currency', width: '100px' },
        { key: 'pctChangeFmt', label: '% Chg', align: 'number', width: '70px' },
        { key: 'sourceFmt', label: 'Source', align: 'text', width: '90px' },
      ],
      rows,
    }],
  };

  return {
    descriptor,
    insights: {
      biggestItem: [...reportRows].sort((left, right) => Math.abs(right.absChange) - Math.abs(left.absChange))[0] || null,
      biggestVendor: summarizePriceChangeGroups(reportRows, 'vendor')[0] || null,
      biggestCategory: summarizePriceChangeGroups(reportRows, 'category')[0] || null,
      vendors: summarizePriceChangeGroups(reportRows, 'vendor'),
      categories: summarizePriceChangeGroups(reportRows, 'category'),
    },
    empty: rows.length === 0,
    summary: buildPriceInsightSummary({ rows: reportRows, rangeLabel, scope, priceInsight }),
    emailLines: [`Up: ${upCount}`, `Down: ${downCount}`, `Total items: ${reportRows.length}`],
    csvFilename: `price-change-report-${startDate}-to-${endDate}.csv`,
    link: { href: '/reports.html', label: 'Open Reports →' },
  };
};

// ── End of Period / actual COGS (from SAVED pnl_reports rows only) ───────────
// Actual COGS is never re-derived from raw counts here: the saved End of Period
// report (pnl_reports) is the source of truth, and the descriptor comes from
// the same buildEopReportDescriptor the Reports page renders saved reports with.
const eopNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
// pnl_reports.report_date is date-only ("2025-08-31"); parse it at noon so the
// label doesn't shift a day back in timezones west of UTC.
const anchorDateOnly = (d) => (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d);
const eopCategoryRows = (row) => (Array.isArray(row?.report_data) ? row.report_data : [])
  .filter((r) => r && r.pnl_category_id != null && !r._row_type);
const eopMetaRow = (row) => (Array.isArray(row?.report_data) ? row.report_data : [])
  .find((r) => r && r._row_type === 'meta') || null;

/** Headline figures of a saved End of Period report (pure). */
export const summarizeEopReportRow = (row) => {
  const catRows = eopCategoryRows(row);
  const metaRow = eopMetaRow(row);
  const sum = (field) => catRows.reduce((s, r) => s + eopNum(r[field]), 0);
  const actualCogs = Number.isFinite(Number(row?.actual_usage_total))
    ? Number(row.actual_usage_total)
    : sum('cost_of_goods_used');
  const netSalesRaw = row?.net_sales_total != null ? row.net_sales_total : metaRow?.totals?.net_sales;
  const cogsPctRaw = row?.aggregate_cogs_pct != null ? row.aggregate_cogs_pct : metaRow?.totals?.actual_cogs_pct;
  return {
    label: row?.period_label || (row?.report_date ? `period ending ${formatWrittenDate(anchorDateOnly(row.report_date))}` : 'End of Period'),
    reportDate: row?.report_date || null,
    beginning: sum('beginning_inventory'),
    purchases: sum('purchases'),
    ending: sum('ending_inventory'),
    transfersAndAdjustments: sum('transfers_in') - sum('transfers_out') + sum('other_adjustments'),
    actualCogs,
    netSales: Number.isFinite(Number(netSalesRaw)) ? Number(netSalesRaw) : null,
    cogsPct: Number.isFinite(Number(cogsPctRaw)) ? Number(cogsPctRaw) : null,
    categoryCogs: new Map(catRows.map((r) => [r.pnl_category_name || 'Uncategorized', eopNum(r.cost_of_goods_used)])),
    completenessStatus: metaRow?.completeness_status || null,
    completenessLabel: metaRow?.completeness_label || null,
  };
};

/**
 * Answer an End of Period / actual COGS question from a saved report row.
 * Pure apart from the timestamp — the row comes from listEopReportsForAI.
 */
export const generateEopCogsReport = ({ eopRow, storeName, createdBy }) => {
  const s = summarizeEopReportRow(eopRow);
  const descriptor = buildEopReportDescriptor(eopRow, {
    storeName,
    createdBy,
    periodEndLabel: eopRow?.report_date ? formatWrittenDate(anchorDateOnly(eopRow.report_date)) : '',
  });

  const empty = eopCategoryRows(eopRow).length === 0;
  const parts = [];
  if (empty) {
    parts.push(`Here is the End of Period data you requested. The saved report “${s.label}” has no category figures.`);
  } else {
    parts.push('Here is the End of Period data you requested.');
    const bridge = s.transfersAndAdjustments !== 0
      ? `beginning ${formatCurrency(s.beginning)} + purchases ${formatCurrency(s.purchases)} ${s.transfersAndAdjustments > 0 ? '+' : '−'} transfers/adjustments ${formatCurrency(Math.abs(s.transfersAndAdjustments))} − ending ${formatCurrency(s.ending)}`
      : `beginning ${formatCurrency(s.beginning)} + purchases ${formatCurrency(s.purchases)} − ending ${formatCurrency(s.ending)}`;
    parts.push(`Actual COGS for ${s.label} was ${formatCurrency(s.actualCogs)} (${bridge}).`);
    if (s.netSales != null) {
      parts.push(`Net sales were ${formatCurrency(s.netSales)}${s.cogsPct != null ? ` — COGS ran ${s.cogsPct}% of net sales` : ''}.`);
    }
    if (s.completenessStatus && s.completenessStatus !== 'complete') {
      parts.push(`Heads up: this report is marked “${s.completenessLabel || 'incomplete'}”, so some figures were not provided.`);
    }
    parts.push('From the saved End of Period report — the same numbers as the Reports page.');
  }

  return {
    descriptor,
    empty,
    summary: parts.join(' '),
    emailLines: [
      `Actual COGS: ${formatCurrency(s.actualCogs)}`,
      `Beginning inventory: ${formatCurrency(s.beginning)}`,
      `Purchases: ${formatCurrency(s.purchases)}`,
      `Ending inventory: ${formatCurrency(s.ending)}`,
      ...(s.netSales != null ? [`Net sales: ${formatCurrency(s.netSales)}`] : []),
      ...(s.cogsPct != null ? [`COGS %: ${s.cogsPct}%`] : []),
    ],
    csvFilename: `end_of_period_${String(eopRow?.report_date || '').slice(0, 10) || 'report'}.csv`,
    link: { href: '/reports.html', label: 'Open End of Period →' },
  };
};

// ── Comparisons ───────────────────────────────────────────────────────────────
const signedCurrency = (v) => `${v >= 0 ? '+' : '−'}${formatCurrency(Math.abs(v))}`;
const signedPct = (v) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(1)}%`;

const comparisonRows = (mapA, mapB) => {
  const categories = [...new Set([...mapA.keys(), ...mapB.keys()])];
  return categories
    .map((category) => {
      const a = mapA.get(category) || 0;
      const b = mapB.get(category) || 0;
      const delta = b - a;
      return { category, a, b, delta, inA: mapA.has(category) };
    })
    .sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta))
    .map((r) => ({
      category: r.category,
      aFmt: formatCurrency(r.a),
      bFmt: formatCurrency(r.b),
      deltaFmt: signedCurrency(r.delta),
      // 'New' only for categories genuinely absent in period A — a negative
      // period-A value (EOP adjustments) is real data, not a new category.
      pctFmt: !r.inA && r.delta !== 0
        ? 'New'
        : (r.a !== 0 ? signedPct((r.delta / Math.abs(r.a)) * 100) : '—'),
      _delta: r.delta,
    }));
};

const largestMoverPhrase = (rows) => {
  if (!rows.length) return '';
  const top = rows[0]; // already sorted by |delta|
  if (!top._delta) return '';
  return ` The largest ${top._delta > 0 ? 'increase' : 'decrease'} was ${top.category} (${top.deltaFmt}).`;
};

/**
 * Month-over-month (or count-vs-count) inventory value comparison. Both sides
 * are valued with the same report math as the Inventory Value report.
 */
export const generateInventoryValueComparison = async ({
  supabase, storeId, sessionA, sessionB, labelA = '', labelB = '', scope = null, storeName, createdBy,
}) => {
  const [a, b] = await Promise.all([
    getReportSessionSummaryForAI({ supabase, session: sessionA, scope }),
    getReportSessionSummaryForAI({ supabase, session: sessionB, scope }),
  ]);
  const nameA = labelA || sessionA.name;
  const nameB = labelB || sessionB.name;
  const rows = comparisonRows(a.byCategory, b.byCategory);
  const delta = b.totalValue - a.totalValue;
  const pct = a.totalValue > 0 ? (delta / a.totalValue) * 100 : null;

  const generatedAt = new Date().toISOString();
  const descriptor = {
    store: { name: storeName },
    report: {
      title: 'Inventory Value Comparison',
      subtitle: `${nameA} vs ${nameB}`,
      counters: [],
      createdBy: createdBy || '',
      generatedAt,
      meta: [
        { label: nameA, value: `${sessionA.name}${sessionA.date ? ` · ${formatShortDate(sessionA.date)}` : ''}` },
        { label: nameB, value: `${sessionB.name}${sessionB.date ? ` · ${formatShortDate(sessionB.date)}` : ''}` },
        ...(scope ? [{ label: 'Filter', value: scope }] : []),
      ],
    },
    stats: [
      { label: nameA, value: formatCurrency(a.totalValue) },
      { label: nameB, value: formatCurrency(b.totalValue) },
      { label: 'Change', value: `${signedCurrency(delta)}${pct != null ? ` (${signedPct(pct)})` : ''}` },
    ],
    sections: [{
      columns: [
        { key: 'category', label: 'Category', align: 'text' },
        { key: 'aFmt', label: nameA, align: 'currency', width: '110px' },
        { key: 'bFmt', label: nameB, align: 'currency', width: '110px' },
        { key: 'deltaFmt', label: 'Change', align: 'currency', width: '100px' },
        { key: 'pctFmt', label: '% Change', align: 'number', width: '90px' },
      ],
      rows: rows.map(({ _delta, ...r }) => r),
      totals: {
        category: 'Total',
        aFmt: formatCurrency(a.totalValue),
        bFmt: formatCurrency(b.totalValue),
        deltaFmt: signedCurrency(delta),
        pctFmt: pct != null ? signedPct(pct) : '—',
      },
    }],
  };

  const direction = delta >= 0 ? 'an increase' : 'a decrease';
  const sessASrc = `${sessionA.name}${sessionA.date ? `, completed ${formatWrittenDate(sessionA.date)}` : ''}`;
  const sessBSrc = `${sessionB.name}${sessionB.date ? `, completed ${formatWrittenDate(sessionB.date)}` : ''}`;
  const summary = rows.length
    ? `Here is the inventory value comparison you requested. ${nameA} inventory value was ${formatCurrency(a.totalValue)} (${sessASrc}). ${nameB} was ${formatCurrency(b.totalValue)} (${sessBSrc}) — ${direction} of ${formatCurrency(Math.abs(delta))}${pct != null ? `, or ${Math.abs(pct).toFixed(1)}%` : ''}.${largestMoverPhrase(rows)}${scope ? ` Filtered to “${scope}”.` : ''}`
    : `I compared the two counts${scope ? ` for items matching “${scope}”` : ''}, but neither has anything to compare.`;

  return {
    descriptor,
    empty: rows.length === 0,
    summary,
    emailLines: [
      `${nameA}: ${formatCurrency(a.totalValue)}`,
      `${nameB}: ${formatCurrency(b.totalValue)}`,
      `Change: ${signedCurrency(delta)}${pct != null ? ` (${signedPct(pct)})` : ''}`,
    ],
    csvFilename: `inventory_value_comparison_${generatedAt.slice(0, 10)}.csv`,
    link: { href: '/reports.html', label: 'Open Reports →' },
  };
};

/** Period-over-period comparison of two SAVED End of Period reports (pure). */
export const generateEopComparisonReport = ({ eopRowA, eopRowB, labelA = '', labelB = '', storeName, createdBy }) => {
  const a = summarizeEopReportRow(eopRowA);
  const b = summarizeEopReportRow(eopRowB);
  const nameA = labelA || a.label;
  const nameB = labelB || b.label;
  const rows = comparisonRows(a.categoryCogs, b.categoryCogs);
  const delta = b.actualCogs - a.actualCogs;
  const pct = a.actualCogs > 0 ? (delta / a.actualCogs) * 100 : null;

  const generatedAt = new Date().toISOString();
  const descriptor = {
    store: { name: storeName },
    report: {
      title: 'End of Period Comparison',
      subtitle: `${nameA} vs ${nameB} — Actual COGS`,
      counters: [],
      createdBy: createdBy || '',
      generatedAt,
      meta: [
        { label: nameA, value: a.reportDate ? formatShortDate(anchorDateOnly(a.reportDate)) : '' },
        { label: nameB, value: b.reportDate ? formatShortDate(anchorDateOnly(b.reportDate)) : '' },
      ],
    },
    stats: [
      { label: `${nameA} COGS`, value: formatCurrency(a.actualCogs) },
      { label: `${nameB} COGS`, value: formatCurrency(b.actualCogs) },
      { label: 'Change', value: `${signedCurrency(delta)}${pct != null ? ` (${signedPct(pct)})` : ''}` },
      ...(a.cogsPct != null && b.cogsPct != null
        ? [{ label: 'COGS % of Net Sales', value: `${a.cogsPct}% → ${b.cogsPct}%` }]
        : []),
    ],
    sections: [{
      title: 'Actual COGS by P&L Category',
      columns: [
        { key: 'category', label: 'P&L Category', align: 'text' },
        { key: 'aFmt', label: nameA, align: 'currency', width: '110px' },
        { key: 'bFmt', label: nameB, align: 'currency', width: '110px' },
        { key: 'deltaFmt', label: 'Change', align: 'currency', width: '100px' },
        { key: 'pctFmt', label: '% Change', align: 'number', width: '90px' },
      ],
      rows: rows.map(({ _delta, ...r }) => r),
      totals: {
        category: 'Total',
        aFmt: formatCurrency(a.actualCogs),
        bFmt: formatCurrency(b.actualCogs),
        deltaFmt: signedCurrency(delta),
        pctFmt: pct != null ? signedPct(pct) : '—',
      },
    }],
  };

  const incomplete = [a, b].filter((s) => s.completenessStatus && s.completenessStatus !== 'complete');
  const summary = [
    'Here is the End of Period comparison you requested.',
    `Actual COGS was ${formatCurrency(a.actualCogs)} for ${nameA} and ${formatCurrency(b.actualCogs)} for ${nameB} — ${delta >= 0 ? 'up' : 'down'} ${formatCurrency(Math.abs(delta))}${pct != null ? ` (${Math.abs(pct).toFixed(1)}%)` : ''}.`,
    ...(a.cogsPct != null && b.cogsPct != null ? [`COGS moved from ${a.cogsPct}% to ${b.cogsPct}% of net sales.`] : []),
    largestMoverPhrase(rows).trim(),
    ...(incomplete.length ? [`Heads up: ${incomplete.map((s) => `“${s.label}” is marked ${s.completenessLabel || 'incomplete'}`).join(' and ')}.`] : []),
    'Both figures come from saved End of Period reports.',
  ].filter(Boolean).join(' ');

  return {
    descriptor,
    empty: rows.length === 0,
    summary,
    emailLines: [
      `${nameA} COGS: ${formatCurrency(a.actualCogs)}`,
      `${nameB} COGS: ${formatCurrency(b.actualCogs)}`,
      `Change: ${signedCurrency(delta)}${pct != null ? ` (${signedPct(pct)})` : ''}`,
    ],
    csvFilename: `eop_comparison_${generatedAt.slice(0, 10)}.csv`,
    link: { href: '/reports.html', label: 'Open End of Period →' },
  };
};
